import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { BillingUserInfoType, ValueType } from "@/types/billing"
import {
  addBillingUser,
  editBillingUserInfo,
  getBillingUsersDropdown,
  getDepartmentDropdown,
  getPositionPerDepartmentDropdown,
  getUsersDropdown,
} from "@/lib/api/billing"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { ComboboxSearch } from "@/components/combobox-search"

import { Icons } from "../icons"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  user_id: z.coerce.number().min(1, "User is required"),
  username: z.string().min(1, "Username  is required"),
  email: z.string().min(1, "Email  is required"),
  first_name: z.string().min(1, "First Name  is required"),
  last_name: z.string().min(1, "Last Name  is required"),
  gender: z.string().min(1, "Gender  is required"),
  mobile_number: z.string().min(1, "Mobile Number  is required"),
  address: z.string().min(1, "Address  is required"),
  department: z.coerce.number().min(1, "Department is required"),
  position: z.coerce.number().min(1, "Position is required"),
  is_meter_reader: z.boolean().optional(),
  initials: z.string(),
})

export default function BillingUserInfoForm({
  onClose,
  billingUserInfo,
  setShowEditBillingUserInfoModal,
}: {
  onClose?: () => void
  billingUserInfo?: BillingUserInfoType
  setShowEditBillingUserInfoModal?: React.Dispatch<
    React.SetStateAction<boolean>
  >
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [departmentId, setDepartmentId] = React.useState<number | undefined>(
    undefined,
  )
  const [isMeterReader, setIsMeterReader] = React.useState<boolean>(false)
  const [selectedUser, setSelectedUser] = React.useState<
    BillingUserInfoType | undefined
  >(undefined)

  const queryConfigs = [
    {
      queryKey: ["departments"],
      queryFn: getDepartmentDropdown,
    },
    {
      queryKey: ["positions", departmentId ?? billingUserInfo?.department_id],
      queryFn: () =>
        getPositionPerDepartmentDropdown(
          departmentId ?? billingUserInfo?.department_id,
        ),
    },
    {
      queryKey: ["billing-users"],
      queryFn: getBillingUsersDropdown,
    },
    {
      queryKey: ["users"],
      queryFn: getUsersDropdown,
    },
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  const [departments, positions, billingUsers, users] = results.map(
    (result) => result.data || [],
  )

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: billingUserInfo
      ? {
          user_id: billingUserInfo?.id,
          department: billingUserInfo?.department_id,
          position: billingUserInfo?.position_id,
          username: billingUserInfo?.username || "",
          email: billingUserInfo?.email || "",
          initials: billingUserInfo?.initials || "",
          first_name: billingUserInfo?.first_name || "",
          last_name: billingUserInfo?.last_name || "",
          gender: billingUserInfo?.gender || "",
          address: billingUserInfo?.address || "",
          mobile_number: billingUserInfo?.mobile_number || "",
          is_meter_reader: billingUserInfo?.is_meter_reader,
        }
      : {
          user_id: selectedUser?.id || 0,
          username: selectedUser?.username || "",
          email: selectedUser?.email || "",
          initials: selectedUser?.initials || "",
          first_name: selectedUser?.first_name || "",
          last_name: selectedUser?.last_name || "",
          gender: selectedUser?.gender || "",
          address: selectedUser?.address || "",
          mobile_number: selectedUser?.mobile_number || "",
          department: 0,
          position: 0,
          is_meter_reader: isMeterReader,
        },
  })

  // Function to handle user selection
  const onSelectUser = (id?: string | number) => {
    const selected = users?.find(
      (user: { id: number; value: ValueType }) => user.id === id,
    )
    if (selected) {
      setSelectedUser(selected.value) // Set selected user
      // Update form fields with selected user's data
      form.setValue("user_id", selected.value.id)
      form.setValue("username", selected.value.username)
      form.setValue("email", selected.value.email)
      form.setValue("first_name", selected.value.first_name)
      form.setValue("initials", selected.value.initials)
      form.setValue("last_name", selected.value.last_name)
      form.setValue("gender", selected.value.gender)
      form.setValue("address", selected.value.address)
      form.setValue("mobile_number", selected.value.mobile_number)
      form.setValue("department", 0) // Assuming default value for department
      form.setValue("position", 0) // Assuming default value for position
    }
  }

  const mutation = useMutation({
    mutationFn: async (data: BillingUserInfoType) => {
      if (billingUserInfo?.id) {
        await editBillingUserInfo(billingUserInfo?.id, data)
      } else {
        await addBillingUser(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billingUserInfoDetails"] })
      toast({
        description: billingUserInfo
          ? "Billing user details have been successfully updated."
          : "New billing user has been successfully added.",
      })
      onClose?.()
      setShowEditBillingUserInfoModal?.(false)
    },
    onError: async (error: any) => {
      let errorMessage = "Please try again."

      try {
        if (error.message) {
          errorMessage = error.message // Extract message from thrown error
        }
      } catch (err) {
        console.error("Error handling failed response:", err)
      }

      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    const payload: BillingUserInfoType = {
      user_id: billingUserInfo ? billingUserInfo?.user_id : values?.user_id,
      username: values?.username,
      email: values?.email,
      initials: values?.initials,
      first_name: values?.first_name,
      last_name: values?.last_name,
      mobile_number: values?.mobile_number,
      address: values?.address,
      gender: values?.gender,
      is_meter_reader: values?.is_meter_reader,
      department_id: values.department,
      position_id: values.position,
    }
    console.log(payload)

    mutation.mutate(payload)
  }

  return (
    <>
      <div className="px-2">
        <DropdownMenuSeparator className="mt-4" />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
          >
            <Card className="my-4 p-6">
              {/* Add flex and gap here */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap">
                  <div className="flex w-full justify-end">
                    <FormField
                      control={form.control}
                      name="is_meter_reader"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Is Meter Reader
                            <span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={field.value || false} // Use form's field value directly
                              onChange={(e) => field.onChange(e.target.checked)} // Update the form state on checkbox change
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid w-full grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-2">
                    {billingUserInfo ? (
                      <FormField
                        control={form.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Username<span className="text-red-600">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <FormField
                        control={form.control}
                        name="user_id"
                        render={({ field }) => (
                          <FormItem className="flex flex-col justify-end">
                            <FormLabel>
                              User<span className="text-red-600">*</span>
                            </FormLabel>
                            <FormControl>
                              <ComboboxSearch
                                field={field}
                                options={
                                  billingUserInfo
                                    ? (billingUsers ?? [])
                                    : (users ?? [])
                                }
                                placeholder="Select user"
                                onSelectId={onSelectUser}
                                idAsValue
                                disabled={!!billingUserInfo}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <FormField
                      control={form.control}
                      name="initials"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Initials</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div
                  className={`${billingUserInfo ? !billingUserInfo : !selectedUser ? "hidden" : ""} grid grid-cols-1 gap-4 md:grid-cols-2`}
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Email<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          First Name<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Last Name<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Gender<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Address<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mobile_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Mobile Number<span className="text-red-600">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel>
                        Department<span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={departments ?? []}
                          placeholder="Select department"
                          onSelectId={(id?: string | number) =>
                            setDepartmentId(id as number)
                          }
                          idAsValue
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-center">
                      <FormLabel>
                        Position<span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxSearch
                          field={field}
                          options={positions ?? []}
                          placeholder="Select position"
                          idAsValue
                          disabled={
                            billingUserInfo
                              ? !billingUserInfo?.department_id
                              : !departmentId
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>

            <div className="flex flex-row justify-end gap-2 md:mr-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  onClose?.()
                  setShowEditBillingUserInfoModal?.(false)
                }}
                className="w-28"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="w-28"
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                {billingUserInfo ? `Edit` : `Add`}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  )
}
